import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Star, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { HalalBadge } from "../components/Navbar";
import { StarPicker } from "../components/Reviews";

const inputCls = "w-full rounded-xl border border-inkline bg-parchment px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-muted2/70 focus:border-spice";

const Shell = ({ children, testid }) => (
  <main className="grain flex min-h-[85vh] items-center justify-center bg-parchment px-4 pt-32 pb-20" data-testid={testid}>
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-xl rounded-3xl border border-inkline bg-cream p-8 sm:p-10">
      {children}
    </motion.div>
  </main>
);

export default function Review() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(undefined);
  const [form, setForm] = useState({ stars: 0, quote: "", name: "", role: "" });
  const [state, setState] = useState("idle");
  const [googleUrl, setGoogleUrl] = useState("");

  useEffect(() => {
    document.title = "Leave a review — HiPlate";
    api.get("/settings/public").then(({ data }) => setGoogleUrl(data.google_review_url || "")).catch(() => {});
    api.get(`/reviews/${token}`)
      .then(({ data }) => {
        setCtx(data);
        setForm((f) => ({ ...f, name: data.customer_name || "" }));
        if (data.submitted) setState("done");
      })
      .catch(() => setCtx(null));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stars) return toast.error("Pick a star rating first.");
    if (form.quote.trim().length < 10) return toast.error("Tell us a little more — at least 10 characters.");
    setState("sending");
    try {
      await api.post(`/reviews/${token}`, { ...form, quote: form.quote.trim(), name: form.name.trim() });
      setState("done");
    } catch (err) {
      toast.error(formatApiError(err));
      setState("idle");
    }
  };

  if (ctx === undefined) {
    return (
      <Shell testid="review-loading">
        <Loader2 size={36} className="mx-auto animate-spin text-spice" />
      </Shell>
    );
  }

  if (ctx === null) {
    return (
      <Shell testid="review-invalid">
        <XCircle size={40} className="mx-auto text-spice" />
        <h1 className="mt-6 text-center font-display text-3xl font-semibold text-ink">That review link isn't valid.</h1>
        <p className="mt-3 text-center text-sm leading-relaxed text-muted2">It may have been copied incorrectly. Reply to our email or call (571) 833-1906 and we'll send a fresh one.</p>
        <div className="mt-8 text-center">
          <Link to="/meal-prep" className="inline-block rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-parchment transition-colors hover:bg-spice" data-testid="review-invalid-cta">See This Week's Menu</Link>
        </div>
      </Shell>
    );
  }

  if (state === "done") {
    return (
      <Shell testid="review-thanks">
        <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-halal/15"><Check size={36} className="text-halal" /></span>
        <h1 className="mt-6 text-center font-display text-3xl font-semibold text-ink" data-testid="review-thanks-title">Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}.</h1>
        <p className="mt-3 text-center text-sm leading-relaxed text-muted2">
          {ctx.submitted && !form.quote ? "You've already shared a review for this order — we read every one." : "Your review is with our team and will appear on the site once approved. It means a lot to a small kitchen."}
        </p>
        <div className="mt-5 flex justify-center"><HalalBadge /></div>
        {googleUrl && (
          <div className="mt-8 rounded-2xl border border-inkline bg-parchment p-5 text-center" data-testid="review-google-box">
            <p className="text-sm text-ink/80">Have 30 more seconds? Posting it on Google helps new neighbors find us.</p>
            <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full border border-inkline bg-cream px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-spice hover:text-spice" data-testid="review-google-btn">
              <Star size={15} className="text-saffron" fill="currentColor" /> Also post on Google
            </a>
          </div>
        )}
        <div className="mt-8 text-center">
          <Link to="/meal-prep" className="inline-block rounded-full bg-spice px-7 py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-ink" data-testid="review-thanks-cta">Order Next Week's Meals</Link>
        </div>
      </Shell>
    );
  }

  const firstName = (ctx.customer_name || "").split(" ")[0];
  return (
    <Shell testid="review-page">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-spice">Quick review · 60 seconds</p>
      <h1 className="mt-3 font-display text-3xl font-semibold leading-tight text-ink sm:text-4xl" data-testid="review-title">
        {firstName ? `${firstName}, how were your meals?` : "How were your meals?"}
      </h1>
      {ctx.items.length > 0 && (
        <p className="mt-3 text-sm leading-relaxed text-muted2" data-testid="review-items">
          You ordered: <span className="text-ink/80">{ctx.items.slice(0, 4).join(", ")}{ctx.items.length > 4 ? ` +${ctx.items.length - 4} more` : ""}</span>
        </p>
      )}
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="review-form">
        <StarPicker value={form.stars} onChange={(stars) => setForm({ ...form, stars })} />
        <textarea
          rows={4}
          value={form.quote}
          onChange={(e) => setForm({ ...form, quote: e.target.value })}
          maxLength={600}
          placeholder="What stood out? A favorite dish, the portions, delivery…"
          className={`${inputCls} resize-none`}
          data-testid="review-quote-input"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={60} placeholder="Your name (shown with the review)" className={inputCls} data-testid="review-name-input" />
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} maxLength={60} placeholder="City or title, e.g. Alexandria, VA (optional)" className={inputCls} data-testid="review-role-input" />
        </div>
        <button
          type="submit"
          disabled={state === "sending"}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-spice px-7 py-4 text-sm font-bold text-parchment transition-colors hover:bg-ink disabled:opacity-60 sm:w-auto"
          data-testid="review-submit-btn"
        >
          {state === "sending" ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />} Submit review
        </button>
        <p className="text-xs leading-relaxed text-muted2">Reviews are read by our team before they appear on the site. Had a problem? Reply to our email or call (571) 833-1906 — we'll make it right.</p>
      </form>
    </Shell>
  );
}
